import { useMemo } from 'react';
import { DataTable, DataTableBody, DataTableFooter } from '@optiaxiom/react';
import { getCoreRowModel, type RowSelectionState, useReactTable } from '@tanstack/react-table';

import type { Notification, Pagination } from '../../types/notification';

import { createColumns } from './columns';

type NotificationTableProps = {
  notifications: Notification[];
  pagination: Pagination | undefined;
  isLoading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  onActivate: (notification: Notification) => void;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  onDelete: (id: string) => void;
};

export function NotificationTable({
  notifications,
  pagination,
  isLoading,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  rowSelection,
  onRowSelectionChange,
  onActivate,
  onMarkRead,
  onMarkUnread,
  onDelete,
}: NotificationTableProps) {
  const columns = useMemo(
    () => createColumns({ onActivate, onMarkRead, onMarkUnread, onDelete }),
    [onActivate, onMarkRead, onMarkUnread, onDelete]
  );

  const totalCount = pagination?.type === 'offset' ? pagination.total_count : 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const paginationState = useMemo(
    () => ({
      pageIndex: page - 1,
      pageSize,
    }),
    [page, pageSize]
  );

  const table = useReactTable({
    columns,
    data: notifications,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
    manualPagination: true,
    rowCount: totalCount,
    pageCount: totalPages,
    state: {
      pagination: paginationState,
      rowSelection,
      columnPinning: { right: ['actions'] },
    },
    onRowSelectionChange,
    onPaginationChange: updater => {
      const newPag = typeof updater === 'function' ? updater(paginationState) : updater;
      if (newPag.pageIndex !== paginationState.pageIndex) {
        onPageChange(newPag.pageIndex + 1);
      }
      if (newPag.pageSize !== paginationState.pageSize) {
        onPageSizeChange(newPag.pageSize);
      }
    },
  });

  return (
    <DataTable table={table}>
      <DataTableBody loading={isLoading} />
      <DataTableFooter />
    </DataTable>
  );
}
